import { useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, TextInput, View } from 'react-native';

import { DocumentTitle } from '@/components/layout/DocumentTitle';
import { SyncStatusCard } from '@/components/settings/SyncStatusCard';
import { Button, Text } from '@/components/ui';
import { fetchCurrentMember, updateMemberProfile } from '@/lib/blocks/api';
import type { MemberRecord } from '@/lib/blocks/types';
import { signOut, useSession } from '@/lib/auth/session';
import { cleanupAllUserData } from '@/lib/dev/cleanup';
import { isDevToolsEnabled } from '@/lib/dev/tools';
import { getSyncEngine } from '@/lib/sync/engine';

export default function SettingsScreen() {
  const router = useRouter();
  const { data: session } = useSession();
  const [member, setMember] = useState<MemberRecord | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchCurrentMember()
      .then((row) => {
        setMember(row);
        setDisplayName(row.displayName);
      })
      .finally(() => setLoading(false));
  }, []);

  async function onSaveName(): Promise<void> {
    const trimmed = displayName.trim();
    if (!trimmed) {
      setError('Name cannot be empty');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await updateMemberProfile({ displayName: trimmed });
      setMember(updated);
      setDisplayName(updated.displayName);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save name');
    } finally {
      setSaving(false);
    }
  }

  async function onSignOut(): Promise<void> {
    await signOut();
    router.replace('/login');
  }

  async function runCleanup(): Promise<void> {
    setCleaning(true);
    setError(null);
    try {
      await cleanupAllUserData();
      await getSyncEngine().resyncAfterWorkspaceWipe();
      router.replace('/app');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clean up user data');
    } finally {
      setCleaning(false);
    }
  }

  function onCleanUpAll(): void {
    const message =
      'Delete ALL workspace data for every member? Family and personal pages, files, databases, favorites, and agent chats will be removed. Empty workspace roots will be recreated. This cannot be undone.';
    if (Platform.OS === 'web') {
      if (window.confirm(message)) {
        void runCleanup();
      }
      return;
    }
    const alertModule = require('react-native') as {
      Alert?: {
        alert: (
          title: string,
          body: string,
          buttons: Array<{ text: string; style?: string; onPress?: () => void }>,
        ) => void;
      };
    };
    if (alertModule.Alert) {
      alertModule.Alert.alert('Clean up all', message, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete everything', style: 'destructive', onPress: () => void runCleanup() },
      ]);
      return;
    }
    void runCleanup();
  }

  const nameDirty = member != null && displayName.trim() !== member.displayName;

  return (
    <>
      <DocumentTitle title="Settings" />
      <View className="flex-1 bg-surface px-page py-page-y">
        <Text variant="h1">Settings</Text>

        {loading ? (
          <ActivityIndicator className="mt-6" />
        ) : (
          <View className="mt-6">
            <Text variant="caption" className="text-muted">
              Display name
            </Text>
            <TextInput
              className="mt-1 rounded-lg border border-border bg-surface px-3 py-2 text-base text-fg"
              onChangeText={setDisplayName}
              placeholder="Your name"
              value={displayName}
            />
            <Text variant="caption" className="mt-1 text-muted">
              Shown in the app. Your private space is always labeled Personal in navigation.
            </Text>
            {nameDirty ? (
              <Pressable
                className="mt-3 self-start rounded-lg bg-accent px-4 py-2 opacity-100 disabled:opacity-50"
                disabled={saving}
                onPress={() => void onSaveName()}
              >
                <Text className="text-base text-fg-inverse">
                  {saving ? 'Saving…' : 'Save name'}
                </Text>
              </Pressable>
            ) : null}
            {error ? <Text className="mt-2 text-sm text-danger">{error}</Text> : null}

            <Text variant="caption" className="mt-6 text-muted">
              {session?.user.email}
            </Text>
            <Button
              className="mt-6 self-start"
              variant="secondary"
              label="Connections"
              onPress={() => router.push('/settings/connections')}
            />
            <Button
              className="mt-4 self-start"
              variant="secondary"
              label="Import from Notion…"
              onPress={() => router.push('/settings/import/notion')}
            />
            <Button
              className="mt-4 self-start"
              variant="secondary"
              label="Import from files…"
              onPress={() => router.push('/settings/import/files')}
            />
            {member?.role === 'admin' ? (
              <>
                <Button
                  className="mt-4 self-start"
                  variant="secondary"
                  label="Manage members"
                  onPress={() => router.push('/settings/members')}
                />
                <Button
                  className="mt-4 self-start"
                  variant="secondary"
                  label="LLM control center"
                  onPress={() => router.push('/settings/llm-models')}
                />
              </>
            ) : null}

            <SyncStatusCard />
          </View>
        )}

        {isDevToolsEnabled() && member?.role === 'admin' ? (
          <>
            <Text variant="caption" className="mt-10 text-muted">
              Development
            </Text>
            <Button
              className="mt-2 self-start"
              disabled={cleaning}
              label={cleaning ? 'Cleaning up…' : 'Clean up workspace'}
              variant="secondary"
              onPress={onCleanUpAll}
            />
            <Text variant="caption" className="mt-1 max-w-md text-muted">
              Wipes all content for every member (family and personal), then re-seeds empty
              workspace roots.
            </Text>
          </>
        ) : null}

        <Button
          className="mt-10 self-start"
          variant="secondary"
          label="Sign out"
          onPress={() => void onSignOut()}
        />
      </View>
    </>
  );
}
